import React from "react";

/**
 * Reusable StatusBadge — light pastel fill, matching border, muted text
 * Usage: <StatusBadge status="approved" /> or <StatusBadge label="Open" color="blue" />
 */

const colorMap = {
  green:   { bg: "#d1fae5", color: "#065f46", border: "#6ee7b7" },
  yellow:  { bg: "#fef3c7", color: "#92400e", border: "#fbbf24" },
  red:     { bg: "#fecaca", color: "#991b1b", border: "#f87171" },
  blue:    { bg: "#bfdbfe", color: "#1e3a8a", border: "#60a5fa" },
  orange:  { bg: "#fed7aa", color: "#7c2d12", border: "#fb923c" },
  gray:    { bg: "#e2e8f0", color: "#334155", border: "#94a3b8" },
  purple:  { bg: "#e9d5ff", color: "#581c87", border: "#a78bfa" },
};

// Predefined status → color mapping
const statusColorMap = {
  // Leaves
  approved: "green",
  pending: "yellow",
  rejected: "red",
  cancelled: "gray",
  // Tickets
  open: "blue",
  "in progress": "orange",
  "in-progress": "orange",
  resolved: "green",
  closed: "gray",
  // Assets
  "new request": "yellow",
  "head approved": "purple",
  "approved": "green",
  "in process": "orange",
  allocated: "green",
  returned: "gray",
  // Priority
  low: "green",
  medium: "yellow",
  high: "red",
};

const badgeStyle = {
  display: "inline-block",
  fontSize: "0.72rem",
  fontWeight: 600,
  padding: "3px 12px",
  borderRadius: "20px",
  borderWidth: "1.5px",
  borderStyle: "solid",
  lineHeight: 1.4,
  whiteSpace: "nowrap",
};

export const StatusBadge = ({ status, label, color, style = {} }) => {
  const displayLabel = label || status || "";
  const resolvedColor = color || statusColorMap[(status || "").toLowerCase()] || "gray";
  const c = colorMap[resolvedColor] || colorMap.gray;

  return (
    <span style={{
      ...badgeStyle,
      backgroundColor: c.bg,
      color: c.color,
      borderColor: c.border,
      ...style,
    }}>
      {displayLabel}
    </span>
  );
};

/**
 * PriorityBadge — same style but specific to priority
 * Usage: <PriorityBadge priority="high" />
 */
export const PriorityBadge = ({ priority }) => {
  const p = (priority || "medium").toLowerCase();
  return <StatusBadge status={p} label={p.charAt(0).toUpperCase() + p.slice(1)} />;
};

/**
 * PriorityPills — shows all 3 pills with active state
 * Usage: <PriorityPills active="medium" />
 */
export const PriorityPills = ({ active, onChange }) => {
  return (
    <div style={{ display: "flex", gap: "8px" }}>
      {["low", "medium", "high"].map((p) => {
        const isActive = active === p;
        const c = colorMap[statusColorMap[p]] || colorMap.gray;
        return (
          <span
            key={p}
            onClick={() => onChange && onChange(p)}
            style={{
              ...badgeStyle,
              backgroundColor: c.bg,
              color: c.color,
              borderColor: c.border,
              fontWeight: isActive ? 600 : 500,
              cursor: onChange ? "pointer" : "default",
              opacity: isActive ? 1 : 0.7,
            }}
          >
            {p.charAt(0).toUpperCase() + p.slice(1)}
          </span>
        );
      })}
    </div>
  );
};

export default StatusBadge;
